import { UPLOAD_PHOTO_TYPES } from "@/lib/enums";

// Safe for the browser as well as the server: no image library is imported here.

const MIME_BY_EXTENSION: Record<string, string> = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
  heic: "image/heic",
  heif: "image/heic",
  pdf: "application/pdf",
  zip: "application/zip",
};

/** MIME type from the file name; null for an extension we do not know. */
export function mimeFromFileName(fileName: string): string | null {
  const ext = fileName.split(".").pop()?.toLowerCase() ?? "";
  return MIME_BY_EXTENSION[ext] ?? null;
}

/** Types a browser sends when it does not know; the file name decides then. */
const UNKNOWN_TYPES = new Set(["", "application/octet-stream"]);

/** The file's type as the browser reports it, or from its extension when it reports none. */
export function fileMimeType(file: { name: string; type: string }): string | null {
  return UNKNOWN_TYPES.has(file.type) ? mimeFromFileName(file.name) : file.type;
}

export type UploadPhotoType = (typeof UPLOAD_PHOTO_TYPES)[number];

export function isUploadType(type: string | null): type is UploadPhotoType {
  return (UPLOAD_PHOTO_TYPES as readonly (string | null)[]).includes(type);
}

/** The accepted photo type of a file, or null when it is not one we take. */
export function uploadTypeOf(file: { name: string; type: string }): UploadPhotoType | null {
  const type = fileMimeType(file);
  return isUploadType(type) ? type : null;
}

/** The `accept` attribute for a file picker. */
export const UPLOAD_ACCEPT = UPLOAD_PHOTO_TYPES.join(",");

export const UPLOAD_TYPES_HINT = "Only JPEG, PNG and WebP photos can be uploaded";

export const HEIC_REFUSED = "HEIC photos cannot be uploaded yet. Export them as JPEG first.";

/** Why a file is refused: HEIC gets its own message, anything else the list of types. */
export function refusalReason(file: { name: string; type: string }): string {
  return fileMimeType(file) === "image/heic" ? HEIC_REFUSED : UPLOAD_TYPES_HINT;
}
